import { GRID_WIDTH, GRID_HEIGHT } from './constants';

// Fisher-Yates Shuffle
export const shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
};

// Get Hex Neighbors (can use imported constants or pass dimensions)
export const getNeighbors = (col, row, gW = GRID_WIDTH, gH = GRID_HEIGHT) => {
    const neighbors = [];
    const evenColDirections = [[0, -1], [1, -1], [1, 0], [0, 1], [-1, 0], [-1, -1]];
    const oddColDirections = [[0, -1], [1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0]];
    const directions = col % 2 === 0 ? evenColDirections : oddColDirections;
    for (const [dc, dr] of directions) {
        const newCol = col + dc;
        const newRow = row + dr;
        if (newCol >= 0 && newCol < gW && newRow >= 0 && newRow < gH) {
            neighbors.push(`${newCol},${newRow}`);
        }
    }
    return neighbors;
};

// Calculate all hexes within a given range from a start point (BFS)
export const calculateHexesInRange = (startCol, startRow, range) => {
    const inRange = new Set();
    const queue = [{ col: startCol, row: startRow, distance: 0 }];
    const startKey = `${startCol},${startRow}`;
    const visited = new Set([startKey]);
    inRange.add(startKey); // Start hex is always in range 0

    while (queue.length > 0) {
        const { col: c, row: r, distance } = queue.shift();
        if (distance < range) {
            const neighbors = getNeighbors(c, r); // Uses default grid dimensions
            for (const neighbor of neighbors) {
                if (!visited.has(neighbor)) {
                    visited.add(neighbor);
                    const [nc, nr] = neighbor.split(',').map(Number);
                    inRange.add(neighbor);
                    queue.push({ col: nc, row: nr, distance: distance + 1 });
                }
            }
        }
    }
    return inRange;
};

// Generate a single 3-hex bush clump for initialization
// (Requires getNeighbors, shuffleArray)
export const generateBushClump = (allowedRows, existingBushes, idolHexIds, gridWidth, gridHeight) => {
    const MAX_ATTEMPTS = 100;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        const randRow = allowedRows[Math.floor(Math.random() * allowedRows.length)];
        const randCol = Math.floor(Math.random() * gridWidth);
        const centerHex = `${randCol},${randRow}`;
        // Avoid placing on existing bushes or idol hexes
        if (existingBushes.has(centerHex) || idolHexIds.has(centerHex)) {
            continue;
        }
        const neighbors = getNeighbors(randCol, randRow, gridWidth, gridHeight);
        // Find neighbors that are also in allowed rows and not already bushes/idols
        const validNeighbors = neighbors.filter(n => {
            const [, nr] = n.split(',').map(Number);
            return allowedRows.includes(nr) && !existingBushes.has(n) && !idolHexIds.has(n);
        });
        // Need at least 2 valid neighbors to form a clump
        if (validNeighbors.length >= 2) {
            const shuffledNeighbors = shuffleArray(validNeighbors);
            const clump = new Set([centerHex, shuffledNeighbors[0], shuffledNeighbors[1]]);
            return clump;
        }
    }
    console.warn("[BUSH GEN] Could not generate a valid bush clump after", MAX_ATTEMPTS, "attempts in rows", allowedRows);
    return new Set(); // Return empty set if failed
};

// Generate hex grid data (coordinates) - suitable for utils or GameBoard
export const generateHexGridGeometry = (gridWidth, gridHeight, hexWidth, hexHeight) => {
    const hexes = [];
    for (let col = 0; col < gridWidth; col++) {
        for (let row = 0; row < gridHeight; row++) {
            const x = col * hexWidth * 0.75;
            const y = row * hexHeight + (col % 2 === 1 ? hexHeight * 0.5 : 0);
            hexes.push({ id: `${col},${row}`, col, row, x, y });
        }
    }
    return hexes;
};

// Convert hex geometry data to SVG polygon points string - suitable for utils or GameBoard
export const hexToPolygonPoints = (hex, hexSize) => {
    let points = [];
    for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i; // 60 degrees per vertex
        const x_i = hex.x + hexSize * Math.cos(angle);
        const y_i = hex.y + hexSize * Math.sin(angle);
        points.push(`${x_i.toFixed(2)},${y_i.toFixed(2)}`);
    }
    return points.join(' ');
};